import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FutureBoardProgress from "./FutureBoardProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  TALK_MAX_CHARS, formatDateTime, fromHistory, getFutureTalkHistory, initialTalkState, postFutureTalk, talkAvailability,
  talkBlocker, talkBlockerKey, talkFailure, talkReducer,
  type AssistQuota,
} from "@chronospark/shared";

type Translate = (key: string, options?: object) => string;

interface Props {
  open: boolean;
  cardId: number;
  accessToken?: string;
  language: string;
  t: Translate;
  /** 残りの回数（OneScene が持つ）。null は、まだ分かっていない */
  quota: AssistQuota | null;
  /** 回答が出たあと・拒否されたあとに、残りの回数を取り直す */
  onUsed: () => void;
  /** サーバーで AI が無効だと分かったとき */
  onDisabled: () => void;
  onClose: () => void;
}

// 「この未来について話す」。本人が送ったときだけ動く。AI は、カードを書き換えない（保存済みの文章を読むだけ）。
// 会話はカードごとに保存され、開き直すと続きから見える。使えないときも、これまでの会話は読める。
function FutureTalkDialog({ open, cardId, accessToken, language, t, quota, onUsed, onDisabled, onClose }: Props) {
  const [state, dispatch] = useReducer(talkReducer, initialTalkState);
  const [text, setText] = useState("");
  const [historyFailed, setHistoryFailed] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const loadedFor = useRef<number | null>(null);
  const counter = useRef(0);
  // state の更新を待たずに、続けて押された送信を止める
  const inFlight = useRef(false);
  const endRef = useRef<HTMLDivElement | null>(null);

  const availability = talkAvailability(quota);
  const sending = state.phase === "sending";
  const blocker = talkBlocker(text, { availability, busy: sending });
  const zone = quota?.timezone ?? "Asia/Tokyo";

  // 初めて開いたとき（カードごとに1回）、これまでの会話を読み込む
  useEffect(() => {
    if (!open || !accessToken || loadedFor.current === cardId) return;
    loadedFor.current = cardId;
    setLoadingHistory(true);
    setHistoryFailed(false);
    getFutureTalkHistory(accessToken, cardId)
      .then(result => dispatch({ type: "loaded", messages: fromHistory(result.messages) }))
      .catch(() => setHistoryFailed(true))
      .finally(() => setLoadingHistory(false));
  }, [open, accessToken, cardId]);

  // 送れなかった文章は、入力欄に戻す（書いた内容を失わない）
  useEffect(() => {
    if (state.restore !== null) {
      setText(state.restore);
      dispatch({ type: "clearRestore" });
    }
  }, [state.restore]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: "end" });
  }, [state.messages]);

  const send = useCallback(async () => {
    if (!accessToken || blocker !== null || inFlight.current) return;
    inFlight.current = true;
    const question = text.trim();
    const id = counter.current++;
    const ids = { userId: `user-${id}`, replyId: `reply-${id}` };
    dispatch({ type: "send", text: question, ...ids });
    setText("");
    try {
      const result = await postFutureTalk(accessToken, cardId, question, (_chunk, accumulated) => dispatch({ type: "token", replyId: ids.replyId, text: accumulated }));
      dispatch({ type: "done", replyId: ids.replyId, text: result.answer });
    } catch (error) {
      const failure = talkFailure(error);
      dispatch({ type: "failed", ...ids, messageKey: failure.messageKey, resetsAt: failure.resetsAt });
      if (failure.disabled) onDisabled();
    } finally {
      inFlight.current = false;
      onUsed(); // 残りの回数を、最新にする
    }
  }, [accessToken, blocker, text, cardId, onDisabled, onUsed]);

  const errorText = !state.messageKey ? ""
    : state.resetsAt ? t("futureBoard.assist.exhausted", { time: formatDateTime(state.resetsAt, language, zone) })
    : t(state.messageKey);
  const exhaustedTime = quota?.resets_at ? formatDateTime(quota.resets_at, language, zone) : "";

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="future-talk-title" maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle id="future-talk-title">{t("futureBoard.talk.title")}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1 }}>{t("futureBoard.talk.intro")}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>{t("futureBoard.talk.privacy")}</Typography>
        {quota?.enabled && (
          <Typography variant="caption" sx={{ display: "block", mb: 1.5 }} aria-live="polite">
            {availability === "exhausted"
              ? t("futureBoard.assist.exhausted", { time: exhaustedTime })
              : t("futureBoard.assist.remaining", { remaining: quota.remaining ?? 0, limit: quota.daily_limit ?? 0 })}
          </Typography>
        )}

        <Box role="log" aria-live="polite" aria-label={t("futureBoard.talk.title")} sx={{ maxHeight: 340, overflowY: "auto", mb: 1.5 }}>
          {loadingHistory && <FutureBoardProgress size={20} aria-label={t("Common.loading")} />}
          {historyFailed && <Alert severity="info" sx={{ mb: 1 }}>{t("futureBoard.talk.historyFailed")}</Alert>}
          {!loadingHistory && state.messages.length === 0 && (
            <Typography variant="body2" color="text.secondary">{t("futureBoard.talk.empty")}</Typography>
          )}
          <Stack spacing={1.25}>
            {state.messages.map(message => (
              <Box
                key={message.id}
                sx={{
                  alignSelf: message.role === "user" ? "flex-end" : "flex-start", maxWidth: "88%",
                  px: 1.5, py: 1, borderRadius: 2, border: "1px solid", borderColor: "divider",
                  bgcolor: message.role === "user" ? "grey.100" : "background.paper",
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {t(message.role === "user" ? "futureBoard.talk.you" : "futureBoard.talk.ai")}
                </Typography>
                {message.pending && !message.content ? (
                  <Stack direction="row" spacing={1} alignItems="center" role="status">
                    <FutureBoardProgress size={14} />
                    <Typography variant="body2">{t("futureBoard.talk.working")}</Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{message.content}</Typography>
                )}
              </Box>
            ))}
          </Stack>
          <div ref={endRef} />
        </Box>

        {state.messageKey && <Alert severity="info" role="alert" sx={{ mb: 1.5 }} onClose={() => dispatch({ type: "dismiss" })}>{errorText}</Alert>}

        <TextField
          fullWidth multiline minRows={2} label={t("futureBoard.talk.inputLabel")} value={text}
          onChange={event => setText(event.target.value)}
          disabled={availability === "disabled"}
          onKeyDown={event => {
            // 日本語の変換中の Enter は送らない。送るのは Ctrl／Cmd + Enter
            if (event.key === "Enter" && (event.ctrlKey || event.metaKey) && !event.nativeEvent.isComposing) {
              event.preventDefault();
              void send();
            }
          }}
          error={blocker === "too_long"}
          helperText={blocker && blocker !== "empty" ? t(talkBlockerKey(blocker), { max: TALK_MAX_CHARS }) : t("futureBoard.talk.note")}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("futureBoard.talk.close")}</Button>
        <Button
          variant="contained" onClick={() => void send()} disabled={blocker !== null}
        >
          {t("futureBoard.talk.send")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default FutureTalkDialog;
